import { createClient } from '@owner/olympus-client'
import { type AxiosError } from 'axios'
import { storeToRefs } from 'pinia'

import { useAuthStore } from '@/auth/useAuthStore'
import { useEmitter } from '@/utils/useEmitter'

import { getActiveIframeSession } from './auth/iframeSession'
import { useCoreStore } from './core/store/useCoreStore'

export const VITE_OLYMPUS_URL = import.meta.env.VITE_OLYMPUS_URL

export const OlympusClient = createClient(
  VITE_OLYMPUS_URL,
  async () => {
    const { activeBrandId } = storeToRefs(useCoreStore())
    const audienceVersion = import.meta.env.APP_SHA || 'unknown'

    const iframeSession = getActiveIframeSession()
    if (iframeSession) {
      return {
        activeBrandId: activeBrandId.value ?? null,
        audience: iframeSession.audience,
        audienceVersion,
        ...(iframeSession.accessToken
          ? {
              bearerToken: iframeSession.accessToken,
              client: 'mobile' as const,
            }
          : { client: 'web' as const, csrfToken: iframeSession.csrfToken }),
      }
    }

    const authStore = useAuthStore()
    return {
      activeBrandId: activeBrandId.value ?? null,
      audience: 'hermes',
      audienceVersion,
      client: 'web',
      csrfToken: authStore.csrfToken ?? undefined,
    }
  },
  {
    onResponseError: async (
      err: AxiosError<{ details?: { logout?: boolean; type?: string } }>
    ) => {
      const status = err.status ?? err.response?.status
      if (status === 401 && err.response?.data?.details?.logout) {
        // Emits to useSessionManager. The payload attributes the logout to the
        // exact endpoint + server-side reason that forced it, so the funnel can
        // tell e.g. an invalid-csrf-access-token reject from a missing-cookie.
        useEmitter.emit('auth.logout', {
          detailType: err.response?.data?.details?.type,
          requestId:
            (err.response?.headers?.['x-request-id'] as string | undefined) ??
            (err.response?.headers?.['x-amzn-trace-id'] as string | undefined),
          source: 'olympus-401-logout',
          status,
          url: err.config?.url,
        })
      }

      // Olympus returns this when a brand-scoped request is denied because the
      // caller isn't a member of the active brand (e.g. access was removed
      // while a tab stayed open on a now-stale activeBrandId). Reconcile the
      // session to an accessible brand rather than surfacing a dead-end 403.
      if (
        err.status === 403 &&
        err.response?.data?.details?.type === 'no-brand-access'
      ) {
        // Handled in useSessionManager
        useEmitter.emit('auth.brand-access-lost')
      }

      // Stale cached CSRF vs the shared `.owner.com` `partner_at` cookie on
      // staging/preview — recovered in useSessionManager instead of looping the
      // 403 fan-out.
      if (
        err.status === 403 &&
        err.response?.data?.details?.type === 'invalid-csrf-access-token'
      ) {
        useEmitter.emit('auth.csrf-invalid', {
          detailType: err.response?.data?.details?.type,
          requestId:
            (err.response?.headers?.['x-request-id'] as string | undefined) ??
            (err.response?.headers?.['x-amzn-trace-id'] as string | undefined),
          source: 'csrf-invalid',
          status: err.status,
          url: err.config?.url,
        })
      }

      throw err
    },
  }
)

export interface PaginatedResult<TResource> {
  limit: number
  page: number
  pageCount: number
  results: TResource[]
  total: number
}
